import React from "react";

import { cn } from "@/utils/cn";

const themes = {
  default: "text-gray-900 dark:text-gray-100",
  danger: "text-danger-600",
  primary: "text-primary-600 dark:text-primary-400",
  secondary: "text-secondary-500",
  muted: "text-muted-foreground",
  subtle: "text-gray-400 dark:text-gray-500",
  success: "text-success-600 dark:text-accent-green",
  placeholder: "text-placeholder",
  inherit: "text-inherit",
  white: "text-white",
};

const weights = {
  normal: "font-normal",
  medium: "font-medium",
  semibold: "font-semibold",
  bold: "font-bold",
};

const sizes = {
  xs: "text-xs",
  sm: "text-sm",
  md: "text-base leading-5",
  xmd: "text-md",
  lg: "text-lg",
  xl: "text-xl",
  "2xl": "text-2xl",
  "3xl": "text-3xl",
  "4xl": "text-4xl",
  "5xl": "text-3xl md:text-5xl",
};

const alignments = {
  left: "text-left",
  center: "text-center",
  right: "text-right",
};

const trackingSizes = {
  normal: "tracking-normal",
  wide: "tracking-wide",
};

const transformProperties = {
  normal: "normal-case",
  uppercase: "uppercase",
};

const families = {
  sans: "font-sans",
  mono: "font-mono",
};

export type Sizes = keyof typeof sizes;
export type Themes = keyof typeof themes;
type Tags =
  | "abbr"
  | "p"
  | "span"
  | "pre"
  | "time"
  | "h1"
  | "h2"
  | "h3"
  | "h4"
  | "h5"
  | "h6"
  | "label"
  | "div"
  | "blockquote";
type Directions = "ltr" | "rtl";

interface TextProps extends Pick<
  React.HTMLAttributes<HTMLParagraphElement>,
  "dangerouslySetInnerHTML" | "id" | "tabIndex" | "lang" | "role"
> {
  /** Text content. */
  children?: React.ReactNode;
  /** How to align the text. */
  align?: keyof typeof alignments;
  /** Extra class names for the outer element. */
  className?: string;
  /** Text direction. */
  direction?: Directions;
  /** Typeface of the text. */
  family?: keyof typeof families;
  /** The "for" attribute specifies which form element a label is bound to. */
  htmlFor?: string;
  /** Font size of the text. */
  size?: Sizes;
  /** HTML element name of the outer element. */
  tag?: Tags;
  /** Theme for the text. */
  theme?: Themes;
  /** Letter-spacing of the text. */
  tracking?: keyof typeof trackingSizes;
  /** Transform (eg uppercase) for the text. */
  transform?: keyof typeof transformProperties;
  /** Whether to truncate the text if its container is too small. */
  truncate?: boolean;
  /** Font weight of the text. */
  weight?: keyof typeof weights;
  /** Tooltip title. */
  title?: string;
  ref?: React.Ref<any>;
}

/** UI-friendly text container with dark mode support. */
const Text = (props: TextProps) => {
  const {
    align,
    className,
    direction,
    family = "sans",
    size = "md",
    tag = "p",
    theme = "default",
    tracking = "normal",
    transform = "normal",
    truncate = false,
    weight = "normal",
    ref,
    ...filteredProps
  } = props;

  const Comp: React.ElementType = tag;

  const alignmentClass = typeof align === "string" ? alignments[align] : "";

  return (
    <Comp
      {...filteredProps}
      ref={ref}
      style={{
        textDecoration: tag === "abbr" ? "underline dotted" : undefined,
        direction,
      }}
      className={cn(
        {
          "cursor-default": tag === "abbr",
          truncate: truncate,
          [sizes[size]]: true,
          [themes[theme]]: true,
          [weights[weight]]: true,
          [trackingSizes[tracking]]: true,
          [families[family]]: true,
          [alignmentClass]: typeof align !== "undefined",
          [transformProperties[transform]]: typeof transform !== "undefined",
          "[&_span.invisible]:inline-block [&_span.invisible]:w-0": true,
        },
        className,
      )}
    />
  );
};

export { Text as default, Text, themes, type TextProps };
